import { useMemo } from 'react'
import { atom, useAtom } from 'jotai'
import { Visibility } from '@skedulo/protobuf-web/dist/ui/crud/v1/actions.js'
import { useApiClient } from '../../index'
import { DEFAULT_VIEW_STATE_ID } from '../constants'
import { scopeToString } from '../scope'
import {
  viewStateStore,
  writeLastActiveIdToLocalStorage,
  getEmptyViewStateForScope,
} from '../store'
import { invalidateViewStateCache } from '../store/view-state-cache'
import {
  useViewStateApiAtom,
  useViewStateDefaultAtom,
  useViewStateLastActiveAtom,
  useViewStatePreferredDefaultAtom,
} from './atoms'
import { useViewState } from './view-state'
import { useViewStateScope } from './view-state-scope'
import { useViewStateType } from './view-state-type'
import type { IViewState } from '../../index'
import type { Scope } from '../types'

/**
 * Exposes funcs that allow you to manage the selected view for a given scope.
 * If no scope is given it will use the active scope from the context.
 */
export const useViewStateManagement = ({
  scope: requestedScope,
}: { scope?: Scope } = {}) => {
  const apiClient = useApiClient()?.viewState
  const scope = useViewStateScope(requestedScope)

  const type = useViewStateType(scope)
  const componentScope = scopeToString(scope)

  const [preferredDefaultId, setPreferredDefaultId] = useAtom(
    useViewStatePreferredDefaultAtom(scope),
    { store: viewStateStore },
  )
  const [lastActiveId, setLastActiveId] = useAtom(
    useViewStateLastActiveAtom(scope),
    { store: viewStateStore },
  )

  const defaultViewStateAtom = useViewStateDefaultAtom(scope)
  const [, setDefaultViewState] = useAtom(defaultViewStateAtom, {
    store: viewStateStore,
  })
  const { apiViewStates, refreshApiViewStates, apiDefaultViewState } =
    useViewStateApiAtom(scope)

  const { viewState, setViewState: setViewStateInternal } = useViewState({
    scope,
  })

  // Atom specifically for the current view state id.
  const idAtom = useMemo(
    () => atom((get) => get(defaultViewStateAtom).id),
    [defaultViewStateAtom],
  )
  idAtom.debugLabel = `viewstate.scope(${componentScope}).id`
  const [currentId] = useAtom(idAtom, { store: viewStateStore })

  // Set the active view state.
  // Accepts the view state or it's ID.
  const setViewState = (viewState: string | IViewState) => {
    if (!apiViewStates) {
      throw new Error('View states not loaded yet')
    }
    const vs =
      typeof viewState === 'string'
        ? apiViewStates.find((it) => {
            return viewState === DEFAULT_VIEW_STATE_ID
              ? apiDefaultViewState
              : it.id === viewState
          })
        : viewState

    if (!vs) {
      throw new Error('Invalid view state id')
    }
    setDefaultViewState(vs)
    // Specifically call setViewStateInternal here as a call to resetViewState
    // can run into late state modification issues and not set this correctly.
    setViewStateInternal(vs)
    setLastActiveId(vs.id)
    writeLastActiveIdToLocalStorage(scope, vs.id)
  }

  // Save the current view state changes and refresh the available view states
  // from API.
  const saveViewState = async (
    name: string | undefined = undefined,
    visibility: Visibility | undefined = undefined,
    from: IViewState | undefined = undefined,
  ) => {
    if (!apiClient) return
    const original = from ?? viewState
    const newViewState: IViewState = {
      ...original,
      name: name ?? original.name,
      visibility: visibility ?? original.visibility,
      viewTypeId: type?.id ?? original.viewTypeId,
      isDefault: false,
    }

    try {
      const updatedViewState = await apiClient.updateViewState({
        viewState: newViewState,
      })
      // Invalidate cache to ensure fresh data on next fetch
      invalidateViewStateCache(scope.type, type?.id, scope.scope)
      await refreshApiViewStates()
      setViewState(updatedViewState)
      return updatedViewState
    } catch (error) {
      throw new Error('Failed to save view state')
    }
  }

  // Save a copy of the current view state and set it as the active view state.
  const saveViewStateAsNew = async (
    name: string,
    visibility: Visibility = Visibility.USER,
    from: IViewState | undefined = undefined,
  ) => {
    if (!apiClient) return
    const original = from ?? viewState
    const newViewState: IViewState = {
      ...original,
      id: '',
      name: name,
      visibility: visibility,
      viewTypeId: type?.id ?? original.viewTypeId,
      systemInformation: undefined,
      isSystemGenerated: false,
      isDefault: false,
    }
    try {
      const createdViewState = await apiClient.createViewState({
        viewState: newViewState,
      })
      // Invalidate cache to ensure fresh data on next fetch
      invalidateViewStateCache(scope.type, type?.id, scope.scope)
      await refreshApiViewStates()
      setViewState(createdViewState)
      return createdViewState
    } catch (error) {
      throw new Error('Failed to save view state as new')
    }
  }

  // This is the saved view that is selected to first display on page load
  // By default, this is the saved view that was last displayed,
  // or either the preferredDefaultId if that has been set as an override
  const findPreferredDefaultViewState = (
    viewStates: IViewState[],
    preferredDefaultId: string | undefined,
    lastActiveId: string | undefined,
  ) => {
    const emptyViewStateForScope = getEmptyViewStateForScope(type, scope)
    const idToFind = preferredDefaultId ?? lastActiveId
    return idToFind
      ? (viewStates.find((vs) => vs.id === idToFind) ?? emptyViewStateForScope)
      : emptyViewStateForScope
  }

  // Delete the given view state
  const deleteViewState = async (id: string = viewState.id) => {
    if (!apiClient) return
    try {
      await apiClient.deleteViewState({
        id: id,
      })
    } catch (error) {
      throw new Error('Failed to delete view state')
    }

    // Invalidate cache to ensure fresh data on next fetch
    invalidateViewStateCache(scope.type, type?.id, scope.scope)
    const newApiViewStates = await refreshApiViewStates()
    const newPreferredDefaultId =
      preferredDefaultId === id ? undefined : preferredDefaultId
    const newLastActiveId = lastActiveId === id ? undefined : lastActiveId
    setPreferredDefaultId(newPreferredDefaultId)
    setLastActiveId(newLastActiveId)
    if (viewState.id === id) {
      setViewState(
        findPreferredDefaultViewState(
          newApiViewStates ?? [],
          newPreferredDefaultId,
          newLastActiveId,
        ),
      )
    }
  }

  return {
    activeViewStateId: currentId,

    defaultViewState: apiDefaultViewState,
    viewStatesList: apiViewStates,
    activateViewState: setViewState,

    saveViewState,
    saveViewStateAsNew,
    refreshViewStatesList: refreshApiViewStates,
    deleteViewState,

    preferredDefaultId,
    setPreferredDefaultId,

    findPreferredDefaultViewState,
  }
}
